import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "./ui/button";
import ShareButton from "./ShareButton";

const navLinks = [
  { name: "Home", path: "/" },
  { name: "The Journey", path: "/journey" },
  { name: "Itinerary", path: "/itinerary" },
  { name: "Pricing", path: "/pricing" },
  { name: "About", path: "/about" },
  { name: "FAQs", path: "/faqs" },
  { name: "Contact", path: "/contact" },
  { name: "Testimonials", path: "/testimonials" },
];

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "bg-[#243b5a]/95 backdrop-blur-md shadow-lg" : "bg-[#243b5a]"
      }`}
    >
      {/* Main nav */}
      <div className="py-4 px-6 md:px-12 lg:px-24">
        <div className="container-max flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <span className="font-serif text-xl md:text-2xl font-semibold text-white tracking-wide">
              Awaken India <span className="text-[#D4AF37]">Tours</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium tracking-wide transition-colors duration-200 ${
                  location.pathname === link.path 
                    ? 'text-[#D4AF37]' 
                    : 'text-white/90 hover:text-[#D4AF37]'
                }`}
                data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-3 ml-6">
            <ShareButton 
              title="Awaken India Tours" 
              description="10-Day Luxury Journey: Taj, Palaces & Himalayas"
            />
            <Link to="/contact">
              <Button 
                className="bg-[#D4AF37] hover:bg-[#B8960C] text-[#243b5a] font-semibold text-sm px-5 py-2 rounded-md transition-all"
                data-testid="inquire-now-btn"
              >
                Inquire
              </Button>
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            className="lg:hidden p-2 text-white"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            data-testid="mobile-menu-btn"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="lg:hidden fixed inset-0 top-[72px] bg-[#243b5a] z-50 overflow-y-auto" data-testid="mobile-menu">
          <nav className="flex flex-col p-6">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`py-4 text-lg border-b border-white/10 ${
                  location.pathname === link.path ? 'text-[#D4AF37] font-semibold' : 'text-white'
                }`}
              >
                {link.name}
              </Link>
            ))}
            <div className="mt-6">
              <Link to="/contact">
                <Button className="bg-[#D4AF37] hover:bg-[#B8960C] text-[#243b5a] font-semibold w-full">
                  Inquire
                </Button>
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
